import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Check, X, Trash2, CalendarDays, Loader2, Image as ImageIcon, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem('eventism_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_BADGE = {
  pending: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
  approved: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
  denied: 'bg-red-500/15 text-red-400 border-red-500/30',
};

function ApprovalList({ kind, items, titleKey, subtitle, onDecide }) {
  return (
    <div className="space-y-2" data-testid={`admin-oc-${kind}`}>
      {items.length === 0 && <p className="text-sm text-muted-foreground">No {kind} applications yet.</p>}
      {items.map((it) => {
        const id = it.captain_id || it.partner_id || it.sponsor_id;
        return (
          <div key={id} className="flex items-center gap-3 rounded-xl border border-border px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="font-heading font-semibold text-sm truncate">{it[titleKey]}</p>
              <p className="text-xs text-muted-foreground truncate">{subtitle(it)}</p>
            </div>
            <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[it.status] || ''}`}>{it.status}</Badge>
            {it.status === 'pending' && (
              <>
                <Button size="sm" onClick={() => onDecide(kind, id, true)} className="bg-emerald-600 hover:bg-emerald-500 text-white hover:text-white" data-testid={`approve-${id}`}><Check className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => onDecide(kind, id, false)} className="text-red-400 border-red-500/30" data-testid={`deny-${id}`}><X className="h-3.5 w-3.5" /></Button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function HeroImagesCard() {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const load = useCallback(() => {
    axios.get(`${API}/october/hero-images`).then((r) => setImages(r.data.images || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/october/admin/hero-images`, fd, { headers: authHeaders() });
      toast.success('Hero photo added to the slideshow');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/october/admin/hero-images/${id}`, { headers: authHeaders() });
      toast.success('Removed');
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <Card data-testid="oc-hero-images-card">
      <CardContent className="p-5 space-y-3">
        <p className="font-heading font-semibold flex items-center gap-2"><ImageIcon className="h-4 w-4 text-fuchsia-400" />Hero slideshow photos</p>
        <p className="text-xs text-muted-foreground">Your real photos show first in the /october-challenge hero slideshow, before the default Sloan's Lake shots.</p>
        <label className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium cursor-pointer hover:bg-accent transition-colors">
          <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="oc-hero-upload-input" />
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload photo
        </label>
        {images.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 pt-1">
            {images.map((img) => (
              <div key={img.image_id} className="relative group aspect-video overflow-hidden rounded-lg bg-muted">
                <img src={img.url} alt="hero" className="h-full w-full object-cover" />
                <button onClick={() => remove(img.image_id)}
                  className="absolute top-1 right-1 rounded-full bg-black/60 p-1 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`oc-hero-delete-${img.image_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminOctoberChallenge() {
  const [data, setData] = useState(null);
  const [ev, setEv] = useState({ name: '', date: '', time: '09:00', location: '', description: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/october/admin/overview`, { headers: authHeaders() })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load — admin login required'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (kind, id, approve) => {
    try {
      await axios.post(`${API}/october/admin/${kind}/${id}/decision`, { approve }, { headers: authHeaders() });
      toast.success(approve ? 'Approved' : 'Denied');
      load();
    } catch { toast.error('Action failed'); }
  };

  const createEvent = async () => {
    if (!ev.name || !ev.date) { toast.error('Name and date required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/october/admin/events`, ev, { headers: authHeaders() });
      toast.success('Kickoff run posted!');
      setEv({ name: '', date: '', time: '09:00', location: '', description: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create');
    } finally { setBusy(false); }
  };

  const deleteEvent = async (id) => {
    try {
      await axios.delete(`${API}/october/admin/events/${id}`, { headers: authHeaders() });
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  if (!data) return <div className="min-h-screen bg-background pt-24 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="admin-october-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-8">
        <div>
          <h1 className="text-2xl font-heading font-bold">October Challenge — Admin</h1>
          <p className="text-sm text-muted-foreground">Approve applications and schedule kickoff runs.</p>
        </div>

        <Card>
          <CardContent className="p-5 space-y-3">
            <p className="font-heading font-semibold flex items-center gap-2"><CalendarDays className="h-4 w-4 text-orange-400" />Post a kickoff run</p>
            <div className="grid sm:grid-cols-2 gap-2">
              <Input placeholder="Run name *" value={ev.name} onChange={(e) => setEv({ ...ev, name: e.target.value })} data-testid="oc-event-name" />
              <div className="grid grid-cols-2 gap-2">
                <Input type="date" value={ev.date} onChange={(e) => setEv({ ...ev, date: e.target.value })} data-testid="oc-event-date" />
                <Input type="time" value={ev.time} onChange={(e) => setEv({ ...ev, time: e.target.value })} data-testid="oc-event-time" />
              </div>
              <Input placeholder="Location" value={ev.location} onChange={(e) => setEv({ ...ev, location: e.target.value })} data-testid="oc-event-location" />
              <Textarea rows={1} placeholder="Description" value={ev.description} onChange={(e) => setEv({ ...ev, description: e.target.value })} data-testid="oc-event-desc" />
            </div>
            <Button onClick={createEvent} disabled={busy} className="rounded-full bg-orange-500 hover:bg-orange-400 text-white hover:text-white" data-testid="oc-event-create-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Post run'}
            </Button>
            <div className="space-y-2 pt-2">
              {data.events.map((e) => (
                <div key={e.oc_event_id} className="flex items-center gap-3 rounded-xl border border-border px-4 py-2.5 text-sm">
                  <span className="flex-1 truncate">{e.name} · {e.date} {e.time} · {e.location}</span>
                  <Badge variant="outline" className="text-[10px]">{e.rsvp_count} RSVPs</Badge>
                  <Button size="sm" variant="outline" onClick={() => deleteEvent(e.oc_event_id)} className="text-red-400 border-red-500/30" data-testid={`oc-event-delete-${e.oc_event_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <HeroImagesCard />

        <Card><CardContent className="p-5 space-y-3">
          <p className="font-heading font-semibold">Run Captain applications</p>
          <ApprovalList kind="captains" items={data.captains} titleKey="name" subtitle={(i) => `${i.location} · ${i.email}`} onDecide={decide} />
        </CardContent></Card>

        <Card><CardContent className="p-5 space-y-3">
          <p className="font-heading font-semibold">Partner applications</p>
          <ApprovalList kind="partners" items={data.partners} titleKey="business_name" subtitle={(i) => `${i.category} · ${i.offer || 'no offer listed'}`} onDecide={decide} />
        </CardContent></Card>

        <Card><CardContent className="p-5 space-y-3">
          <p className="font-heading font-semibold">Sponsor inquiries</p>
          <ApprovalList kind="sponsors" items={data.sponsors} titleKey="company_name" subtitle={(i) => `${i.tier} · $${(i.pledged_amount || 0).toLocaleString()}`} onDecide={decide} />
        </CardContent></Card>
      </div>
    </div>
  );
}
